// Image registry. Every photo entry is documented in PHOTO-ASSET-REGISTER.md
// (Pexels source page, photographer, licence review status).
export type PhotoId =
  | 'london-hunt' | 'london-houses' | 'street-piano' | 'street-concert'
  | 'campus-walk' | 'campus-steps' | 'campus-group'
  | 'concert-lights' | 'concert-crowd' | 'night-market' | 'mural-painter'
  | 'volunteer' | 'cleanup' | 'graffiti-wall'
  | 'coin-tokyo' | 'coin-japan' | 'coin-james' | 'coin-generic';

export interface PhotoMeta {
  kind: 'photo' | 'artwork';
  /** Path under /public without width suffix (photos) or full file (artwork). */
  file: string;
  widths: readonly number[];
  alt: string;
}

const W = [540, 1080] as const;
const photo = (file: string, alt: string): PhotoMeta => ({ kind: 'photo', file: `photos/${file}`, widths: W, alt });
const art = (file: string, alt: string): PhotoMeta => ({ kind: 'artwork', file: `units/${file}`, widths: [], alt });

export const PHOTOS: Record<PhotoId, PhotoMeta> = {
  'london-hunt': photo('london-hunt', 'People outside a traditional pub on a busy London street'),
  'london-houses': photo('london-houses', 'Brick terraced houses on a residential London street'),
  'street-piano': photo('street-piano', 'A street pianist playing for a crowd'),
  'street-concert': photo('street-concert', 'Street musicians performing to an outdoor audience in the sun'),
  'campus-walk': photo('campus-walk', 'Three students walking together on a university campus'),
  'campus-steps': photo('campus-steps', 'Students studying together on outdoor campus steps'),
  'campus-group': photo('campus-group', 'Students walking towards a historic university building'),
  'concert-lights': photo('concert-lights', 'Concert crowd under orange stage lights'),
  'concert-crowd': photo('concert-crowd', 'Crowd with raised hands at a night concert'),
  'night-market': photo('night-market', 'Food stalls lit up at a night market'),
  'mural-painter': photo('mural-painter', 'An artist painting a mural on an outdoor wall'),
  volunteer: photo('volunteer', 'A volunteer collecting litter on a city pavement'),
  cleanup: photo('cleanup', 'Volunteers clearing litter in a forest'),
  'graffiti-wall': photo('graffiti-wall', 'A colourful graffiti wall with an artist at work'),
  'coin-tokyo': art('tokyo-3a-beta.png', 'Tokyo UNIT coin artwork, 3A beta'),
  'coin-japan': art('country-japan.png', 'Japan Country UNIT coin artwork'),
  'coin-james': art('name-james.png', 'James Name UNIT coin artwork'),
  'coin-generic': art('unitcoin.png', 'Generic UNIT coin'),
};

const base = (): string => import.meta.env.BASE_URL ?? '/';

export function photoUrl(id: PhotoId, width = 540): string {
  const m = PHOTOS[id];
  return m.widths.length ? `${base()}${m.file}-${width}.webp` : `${base()}${m.file}`;
}

export function photoSrcSet(id: PhotoId): string | undefined {
  const m = PHOTOS[id];
  return m.widths.length ? m.widths.map((w) => `${base()}${m.file}-${w}.webp ${w}w`).join(', ') : undefined;
}

export const isArtwork = (id: PhotoId): boolean => PHOTOS[id].kind === 'artwork';
